"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "appliance-energy-calculator",
  "refrigerator-lifespan-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

export function CarbonFootprintCalculatorPageClient() {
  const meta = TOOL_METADATA["refrigerator-carbon-footprint-calculator"]

  const [applianceType, setApplianceType] = useState("refrigerator")
  const [size, setSize] = useState("medium")
  const [energySource, setEnergySource] = useState("grid-average")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const baseUsage: Record<string, Record<string, number>> = {
      refrigerator: { small: 350, medium: 500, large: 700 },
      freezer: { small: 300, medium: 450, large: 600 },
      "wine-cooler": { small: 200, medium: 300, large: 400 },
    }

    const annualKwh = baseUsage[applianceType]?.[size] || 500

    const carbonFactors: Record<string, number> = {
      coal: 2.2,
      "natural-gas": 0.9,
      "grid-average": 1.2,
      renewable: 0.1,
      solar: 0.05,
    }

    const carbonFactor = carbonFactors[energySource] || 1.2
    const carbonPoundsPerYear = annualKwh * carbonFactor
    const carbonTonsPerYear = carbonPoundsPerYear / 2000
    const treesNeeded = Math.ceil(carbonPoundsPerYear / 48)

    let recommendation = ""
    if (carbonTonsPerYear > 0.5) {
      recommendation = "Consider an ENERGY STAR upgrade or a cleaner grid mix."
    } else if (carbonTonsPerYear > 0.2) {
      recommendation = "Reasonable footprint with room to improve."
    } else {
      recommendation = "Low-impact appliance — well done."
    }

    return {
      annualKwh,
      carbonPoundsPerYear: Math.round(carbonPoundsPerYear),
      carbonTonsPerYear,
      treesNeeded,
      recommendation,
    }
  }, [applianceType, size, energySource])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="What's the"
      heroTitleItalic="real"
      heroTitlePost="climate cost of your fridge?"
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~30 sec", "EPA eGRID + DOE EnergyGuide"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "1.2", label: "lb CO₂ / kWh", note: "EPA eGRID US avg" },
            { value: "500", label: "kWh / yr", note: "Mid-size fridge" },
            { value: "2.2", label: "lb CO₂ / kWh", note: "Coal-heavy grid" },
            { value: "48", label: "lb CO₂ / tree / yr", note: "USDA offset" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "refrigerator-carbon-footprint-calculator")}
      cta={{
        title: "Cut your kitchen carbon — shop ENERGY STAR fridges",
        body: "Browse the ENERGY STAR collection at Fridge.com — certified models that use 9%+ less energy than standard.",
        buttonLabel: "Browse ENERGY STAR fridges",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Three inputs.
                <br />
                One climate number.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Baseline kWh from DOE EnergyGuide categories, multiplied by your grid&apos;s CO₂ intensity (EPA eGRID).
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Appliance type
                  </label>
                  <select
                    value={applianceType}
                    onChange={(e) => setApplianceType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="refrigerator">Full-size refrigerator</option>
                    <option value="freezer">Standalone freezer</option>
                    <option value="wine-cooler">Wine cooler</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Size
                  </label>
                  <select
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="small">Small (under 18 cu ft)</option>
                    <option value="medium">Medium (18–25 cu ft)</option>
                    <option value="large">Large (over 25 cu ft)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Grid energy source
                  </label>
                  <select
                    value={energySource}
                    onChange={(e) => setEnergySource(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="coal">Coal-heavy</option>
                    <option value="natural-gas">Natural gas</option>
                    <option value="grid-average">US grid average</option>
                    <option value="renewable">Renewable mix</option>
                    <option value="solar">Solar</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Check your utility&apos;s fuel-mix disclosure.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Annual carbon footprint
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.carbonPoundsPerYear ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">lb CO₂ / yr</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendation ?? "Enter appliance details."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Annual kWh</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.annualKwh ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Trees to offset</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.treesNeeded ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Cut the carbon"
          title="Counter-depth fridges to compare"
          description="Full-size capacity in a smaller footprint. The ENERGY STAR badge marks certified models, which use at least 9% less energy than the federal minimum."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                EPA eGRID factors. <em className="italic text-[#ff9951]">DOE</em> baselines.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Operational emissions = annual kWh × grid CO₂ intensity. Grid factors come from the EPA eGRID database; baseline kWh comes from DOE EnergyGuide test data by category.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Baseline kWh by appliance & size", d: "Mid-size fridge ~500 kWh/yr; large ~700; small ~350. Standalone freezer and wine cooler use lower category baselines." },
                { n: "02", t: "Grid CO₂ intensity (lb / kWh)", d: "EPA eGRID: coal-heavy ~2.2, natural gas ~0.9, US average ~1.2, renewable mix ~0.1, solar ~0.05." },
                { n: "03", t: "Tree offset", d: "USDA Forest Service estimates a mature tree absorbs ~48 lb CO₂ per year. Trees needed = lb CO₂ ÷ 48." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door fridges to compare"
          description="Look for an inverter (variable-speed) compressor: it runs quieter and draws less peak power than a single-speed one."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
